import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import viVN from 'antd/locale/vi_VN';

// Auth & Profile Components
import Home from './components/Home/Home';
import Register from './components/Register';
import EmailVerification from './components/EmailVerification';
import Login from './components/Login/Login';
import ForgotPassword from './components/ForgotPassword/ForgotPassword';

// Subject Components
import SubjectList from './components/Subject/SubjectList';

// Period Components
import PeriodList from './components/Period/PeriodList';

// Unverified Message Component
import UnverifiedMessage from './components/UnverifiedMessage/UnverifiedMessage';

// Password Reset Component
import PasswordReset from './components/PasswordReset/PasswordReset';

// Student Profile Component
import StudentProfile from './components/StudentProfile/StudentProfile';

// Attachments Component
import Attachments from './components/Attachments/Attachments';

// Payment Component
import Payment from './components/Payment/Payment';

// Result Lookup Component
import ResultLookup from './components/ResultLookup/ResultLookup';

// Student List Component
import StudentList from './components/StudentList/StudentList';

// Email Logs Component
import EmailLogs from './components/EmailLogs/EmailLogs';

import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import AdminDashboard from './components/AdminDashboard/AdminDashboard';
import PaymentInfo from './components/StudentProfile/subComponents/PaymentInfo';
import StudentProfileDetail from './components/StudentProfile/StudentProfileDetail';
import SubjectCombinationManager from './components/AdminDashboard/SubjectCombinationManager';
import AdminAdmissionScores from './components/AdminDashboard/AdminAdmissionScores';
import AdminBenchmarkManager from './components/AdminDashboard/AdminBenchmarkManager';
import AdminIeltsList from './components/AdminDashboard/AdminIeltsList';
import ProfileChecklist from './components/ProfileChecklist/ProfileChecklist';
import AdminProvincialPrizeList from './components/AdminDashboard/AdminProvincialPrizeList';
import AdminSubjectManager from './components/AdminDashboard/AdminSubjectManager';
import AdminSubjectTypeManager from './components/AdminDashboard/AdminSubjectTypeManager';
import EmailReminders from './components/AdminDashboard/EmailReminders';
import ProfileStats from './components/ProfileStats/ProfileStats';
import AdmissionEvaluatorPage from './components/AdmissionEvaluator/AdmissionEvaluatorPage';
import AdminAdmissionPrograms from './components/AdminDashboard/AdminAdmissionPrograms';
import PaymentLogsManagement from './components/AdminDashboard/PaymentLogsManagement';
import AdminAdmissionResults from './components/AdminDashboard/AdminAdmissionResults';

const App = () => {
    return (
        <ConfigProvider
            locale={viVN}
            theme={{
                token: {
                    colorPrimary: '#1890ff',
                    zIndexPopupBase: 9999, // Ensure modals and messages appear on top
                },
            }}
        >
            <Routes>
                {/* Default Route */}
                <Route path="/" element={<Navigate to="/home" replace />} />
            <Route path="/home" element={<Home />} />
            
            {/* Authentication & Profile Routes */}
            <Route path="/register" element={<Register />} />

            <Route path="/verify-email/:id/:hash" element={<EmailVerification />} />
            
            {/* Authentication Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            
            {/* Subject Routes */}
            <Route path="/subjects" element={<SubjectList />} />
            
            {/* Period Routes */}
            <Route path="/periods" element={<PeriodList />} />

            {/* Unverified Message Route */}
            <Route path="/unverified" element={<UnverifiedMessage />} />

            <Route path="/password-reset/:token" element={<PasswordReset />} />

            {/* Student Profile Route */}
            <Route path="/student-profile" element={<StudentProfile />} />

            {/* Attachments Route - Redirect to student-profile (attachments now integrated) */}
            <Route path="/attachments" element={<Navigate to="/student-profile" replace />} />

            {/* Profile Checklist Route */}
            <Route path="/checklist" element={<ProfileChecklist />} />

            {/* Payment Route */}
            <Route path="/payment" element={<Payment />} />

            {/* Result Lookup Route */}
            <Route path="/result-lookup" element={<ResultLookup />} />

            {/* Payment Route */}
            <Route path="/payment-info" element={<PaymentInfo />} />

            {/* Admin Routes */}
            <Route 
                path="/admin" 
                element={
                    <ProtectedRoute>
                        <AdminDashboard />
                    </ProtectedRoute>
                } 
            />
            <Route
                path="/admin/student-list"
                element={
                    <ProtectedRoute permission="view_student_list">
                        <StudentList />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/email-logs"
                element={
                    <ProtectedRoute permission="view_email_logs">
                        <EmailLogs />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/admin/subject-combinations"
                element={
                    <ProtectedRoute permission="manage_subjects">
                        <SubjectCombinationManager />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/admin/admission-scores"
                element={
                    <ProtectedRoute permission="view_admission_scores">
                        <AdminAdmissionScores />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/admin/benchmarks"
                element={
                    <ProtectedRoute permission="manage_benchmarks">
                        <AdminBenchmarkManager />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/admin/students-ielts"
                element={
                    <ProtectedRoute permission="view_ielts">
                        <AdminIeltsList />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/admin/students-provincial-prize"
                element={
                    <ProtectedRoute permission="view_prizes">
                        <AdminProvincialPrizeList />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/admin/subjects"
                element={
                    <ProtectedRoute permission="manage_subjects">
                        <AdminSubjectManager />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/admin/subject-types"
                element={
                    <ProtectedRoute permission="manage_subjects">
                        <AdminSubjectTypeManager />
                    </ProtectedRoute>
                }
            />

            <Route path="/student-profile/:id" element={<StudentProfileDetail />} />

            <Route
                path="/admin/email-reminders"
                element={
                    <ProtectedRoute permission="send_reminders">
                        <EmailReminders />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/profile-stats"
                element={
                    <ProtectedRoute permission="view_statistics">
                        <ProfileStats />
                    </ProtectedRoute>
                }
            />

            {/* Quản lý ngành đánh giá cơ hội (admin) */}
            <Route
                path="/admin/admission-programs"
                element={
                    <ProtectedRoute permission="manage_benchmarks">
                        <AdminAdmissionPrograms />
                    </ProtectedRoute>
                }
            />

            {/* Đánh giá cơ hội trúng tuyển (thí sinh đăng nhập) */}
            <Route path="/admission-evaluator" element={<AdmissionEvaluatorPage />} />

            {/* Quản lý payment logs BIDV (admin) */}
            <Route
                path="/admin/payment-logs"
                element={
                    <ProtectedRoute permission="manage_payment_logs">
                        <PaymentLogsManagement />
                    </ProtectedRoute>
                }
            />

            {/* Kết quả trúng tuyển (tra cứu) (admin) */}
            <Route
                path="/admin/admission-results"
                element={
                    <ProtectedRoute permission="manage_admission_results">
                        <AdminAdmissionResults />
                    </ProtectedRoute>
                }
            />
        </Routes>
        </ConfigProvider>
    );
};

export default React.memo(App);
